<template>
  <div
    v-if="result"
    class="gept-report-page"
    :class="{ 'gept-report-page--speaking': isSpeakingReport }"
  >
    <ReportModal
      v-if="showfirstModal"
      :showfirstModal="showfirstModal"
      @closeModal="closeModal"
      :type="result.type"
      :selectItemIdx="selectItemIdx"
      :selectChildIdx="selectChildIdx"
      :itemData="selectItemData"
      :itemName="selectItemName"
    />

    <div ref="captureRef" class="gept-report-capture">
      <div class="report-tabs" v-if="reportTabs.length">
        <button
          v-for="tab in reportTabs"
          :key="tab.key"
          type="button"
          class="report-tab"
          @click="scrollToReportGroup(tab.key)"
        >
          {{ tab.label }}
        </button>
      </div>

      <section class="report-title-panel">
        <h1>{{ reportTitle }}</h1>
      </section>

      <section class="report-summary" v-if="showReportSummary">
        <div class="report-score-card">
          <span>總分</span>
          <strong>{{ displayTotalScore }}</strong>
        </div>

        <div class="report-score-table">
          <table>
            <thead>
              <tr>
                <th v-for="header in summaryHeaders" :key="header">{{ header }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in summaryRows" :key="row.key">
                <template v-if="isSpeakingReport">
                  <td>{{ row.typeLabel }}</td>
                  <td>{{ row.groupLabel }}</td>
                  <td>{{ row.scoreLabel }}</td>
                </template>
                <template v-else>
                  <td v-if="row.showGroup" :rowspan="row.groupRowspan">{{ row.groupLabel }}</td>
                  <td>{{ row.typeLabel }}</td>
                  <td>{{ row.scoreLabel }}</td>
                </template>
              </tr>
            </tbody>
          </table>
          <p class="report-score-note text-left" v-if="summaryNote">{{ summaryNote }}</p>
        </div>
      </section>

      <div class="report-legend" v-if="isSpeakingReport">
        <div class="legend-item">
          <span class="legend-dot good-bg"></span>
          <p>很棒</p>
        </div>
        <div class="legend-item">
          <span class="legend-dot acceptable-bg"></span>
          <p>尚可</p>
        </div>
        <div class="legend-item">
          <span class="legend-dot strengthened-bg"></span>
          <p>待加強</p>
        </div>
      </div>

      <template v-for="(section, sectionIndex) in visibleReportSections">
        <section
          :id="section.anchorId"
          :key="section.key"
          class="report-section"
          :class="{
            'report-section--paragraph': section.group === 'paragraph',
            'report-section--speaking': isSpeakingReport
          }"
        >

          <h2
            class="report-section__title text-left"
            v-if="showSectionTitle(section)"
          >
            {{ sectionDisplayTitle(sectionIndex, section.name) }}
          </h2>

          <article
            v-for="(question, questionIndex) in section.data"
            :key="question.key"
            class="qa-card"
          >
            <div class="qa-row qa-row--question">
              <div class="qa-label qa-label--question">{{ questionLabel(questionIndex) }}</div>
              <div class="qa-body">
                <button
                  v-if="questionAudio(question)"
                  type="button"
                  class="audio-button"
                  @click.prevent="setAudio(questionAudio(question))"
                >
                  <img src="@/assets/image/gept/m_play.png" alt="play question">
                </button>

                <div class="qa-main">
                  <div class="question-image" v-if="questionImage(question)">
                    <img :src="questionImage(question)" alt="question">
                  </div>
                  <p
                    class="qa-text qa-text--question"
                    v-if="questionTitle(question)"
                    v-html="lineBreak(questionTitle(question))"
                  ></p>
                </div>
              </div>
            </div>

            <div
              class="reference-toggle"
              v-if="showCollapsibleReference(section, question)"
              @click="toggleMenu(question.sourceIndex)"
            >
              <span>參考答案</span>
              <img
                :class="{ 'transform rotate-180': selectedReferenceAnswer === question.sourceIndex }"
                src="@/assets/image/gept/arrow_down.png"
                alt=""
              >
            </div>
            <div
              class="reference-panel"
              v-if="showCollapsibleReference(section, question)"
              :class="{ 'd-block': selectedReferenceAnswer === question.sourceIndex }"
            >
              <p>{{ questionDescription(question) }}</p>
              <div class="reference-translation" v-if="questionGuide(question)">
                <p class="font-semibold">中文翻譯：</p>
                <p>{{ questionGuide(question) }}</p>
              </div>
            </div>

            <div class="qa-row qa-row--answer">
              <div class="qa-label qa-label--answer">{{ answerLabel(questionIndex) }}</div>
              <div class="qa-body qa-body--answer">
                <button
                  v-if="answerAudio(question)"
                  type="button"
                  class="audio-button"
                  @click.prevent="setAudio(answerAudio(question))"
                >
                  <img src="@/assets/image/gept/voice_play.png" alt="play answer">
                </button>

                <div class="qa-main">
                  <div class="answer-count" v-if="showWordCount(section, question)">
                    作答字數：{{ countEnglishWords(answerPlainText(question)) }} 字
                  </div>

                  <p class="qa-text qa-text--answer" v-if="hasScoredText(question)">
                    <span
                      v-for="(word, wordIndex) in scoredWords(question)"
                      :key="wordIndex"
                      :class="word.scoreClass"
                    >
                      {{ word.text }}
                    </span>
                  </p>
                  <p
                    class="qa-text qa-text--answer"
                    v-else-if="answerHtml(question)"
                    v-html="answerHtml(question)"
                  ></p>
                  <p class="qa-text qa-text--empty" v-else>尚無作答內容</p>
                </div>

                <button
                  v-if="shouldShowReviewButton(section)"
                  type="button"
                  class="review-button"
                  :disabled="!question.result"
                  @click="toggleReportByQuestion(section, question)"
                >
                  批改
                </button>
              </div>
            </div>

            <div
              class="reference-answer-card"
              v-if="showReferenceAnswerCard(section, question)"
            >
              <div class="reference-answer-card__label">參考<br>答案</div>
              <div class="reference-answer-card__body">
                <p v-if="questionAnswer(question)">{{ questionAnswer(question) }}</p>
                <p v-if="questionDescription(question) && section.name === '段落寫作'">
                  {{ questionDescription(question) }}
                </p>
              </div>
            </div>
          </article>
        </section>
      </template>
    </div>

    <div class="report-actions" :class="{ 'report-actions--speaking': isSpeakingReport }" v-if="isSpeakingReport || ai_sid">
      <template v-if="isSpeakingReport && reportActionButtons.length">
        <button
          v-for="button in reportActionButtons"
          :key="button.key"
          type="button"
          class="report-action-button"
          @click="handleReportAction(button)"
        >
          {{ button.label }}
        </button>
      </template>
      <button v-else type="button" @click="reStart" class="download-button">關閉視窗</button>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import api from '@/api'
  import ReportModal from '@/components/GEPT/ReportModal'
  import allJs from '@/components/all.js'
  import formal_examJs from '@/components/formal_exam.js'
  export default {
    name: 'report',

    data () {
      return {
        showfirstModal: false,

        sound_effect: null,

        selectItemIdx: -1,
        selectChildIdx: -1,

        result: null,

        selectedReferenceAnswer: -1,
        selectedReferenceAnswer2: -1,
        translate: null,

        tags: null,
        intervalId: null
      }
    },
    props: {
    },
    components: {
      ReportModal
    },
    mixins: [
      allJs,
      formal_examJs
    ],
    computed: {
      ...mapGetters([
        'authorized',
        'ai_sid',
        'ai_uuid'
      ]),
      isFormalReport () {
        return this.$route.name === 'gept_report_formal' || this.$route.name === 'quiz_report_formal'
      },

      isPartReport () {
        return this.$route.name === 'gept_report_part'
      },

      isWritingReport () {
        return this.result?.type === 'writing'
      },

      isSpeakingReport () {
        return this.result?.type === 'speaking'
      },

      isIntermediateWritingReport () {
        if (!this.isWritingReport) return false
        return this.result?.level === '中級' ||
          this.result?.part === '中譯英' ||
          this.result?.part === '英文作文' ||
          this.visibleReportSections.some(section => ['中譯英', '英文作文'].indexOf(section.name) > -1)
      },

      reportTypeLabel () {
        if (this.isSpeakingReport) return '口說'
        if (this.isWritingReport) return '寫作'
        return ''
      },

      reportRoundName () {
        if (!this.result?.name) return ''
        return String(this.result.name)
          .replace(/^GEPT\s*/, '')
          .replace(new RegExp(`^${this.result.level || ''}\\s*`), '')
          .replace(/^(口說|寫作)\s*/, '')
          .replace(/作答報告/g, '')
          .trim()
      },

      reportTitle () {
        if (!this.result) return ''

        return [
          'GEPT',
          this.result.level,
          this.reportTypeLabel,
          this.reportRoundName,
          '作答報告'
        ].filter(Boolean).join(' ')
      },

      visibleReportSections () {
        if (!this.result) return []

        const rawSections = this.isFormalReport && Array.isArray(this.result.part)
          ? this.result.part.map((section, index) => ({
            name: section.name,
            data: section.data,
            sourceIndex: index
          }))
          : [{
            name: this.result.part,
            data: this.result.data,
            sourceIndex: -1
          }]

        const sections = rawSections
          .map(section => this.normalizeReportSection(section))
          .filter(section => section.data.length > 0)

        const groupCounts = sections.reduce((counts, section) => {
          counts[section.group] = (counts[section.group] || 0) + 1
          return counts
        }, {})
        const seenGroups = {}

        return sections.map(section => {
          const isFirstGroup = !seenGroups[section.group]
          seenGroups[section.group] = true
          return {
            ...section,
            isFirstGroup,
            hasManyInGroup: groupCounts[section.group] > 1,
            anchorId: isFirstGroup ? this.sectionAnchorId(section.group) : null
          }
        })
      },

      showWritingSummary () {
        return this.isWritingReport && this.summaryRows.length > 0
      },

      showSpeakingSummary () {
        return this.isSpeakingReport && this.summaryRows.length > 0
      },

      showReportSummary () {
        return this.displayTotalScore !== null && (this.showWritingSummary || this.showSpeakingSummary)
      },

      summaryHeaders () {
        if (this.isIntermediateWritingReport) {
          return ['分項測驗', '評分項目', '得分']
        }

        return this.isSpeakingReport
          ? ['評分項目', '評分部分', '得分']
          : ['評分部分', '測驗題型', '得分']
      },

      reportTabs () {
        return []
      },

      summaryRows () {
        if (this.isSpeakingReport) return this.speakingSummaryRows
        if (this.isWritingReport) return this.writingSummaryRows
        return []
      },

      writingSummaryRows () {
        if (this.isIntermediateWritingReport) {
          const rows = [
            {
              key: 'chinese-to-english',
              groupLabel: '第一部分：中譯英（40%）',
              groupRowspan: 1,
              showGroup: true,
              typeLabel: '用字遣詞、文法、拼字、標點符號及大小寫',
              scoreKeys: ['avg_translation_score', 'Avg_Translation_score', 'avgTranslationScore']
            },
            {
              key: 'english-composition',
              groupLabel: '第二部分：英文作文（60%）',
              groupRowspan: 1,
              showGroup: true,
              typeLabel: '內容適切性、組織、字彙及句型、文法、拼字及標點符號',
              scoreKeys: ['avg_writing_score', 'Avg_Writing_score', 'avgWritingScore']
            }
          ]

          return rows.map(row => ({
            ...row,
            scoreLabel: this.scoreLabelByKeys(row.scoreKeys)
          }))
        }

        const rows = [
          {
            key: 'sentence-rewrite',
            groupLabel: '第一部分:單句寫作(50%)',
            groupRowspan: 3,
            showGroup: true,
            typeLabel: 'A.句子改寫',
            scoreKeys: ['sum_sentence_rewrite_score', 'sum_SentenceRewrite_score']
          },
          {
            key: 'sentence-merge',
            groupLabel: '第一部分:單句寫作(50%)',
            groupRowspan: 3,
            showGroup: false,
            typeLabel: 'B.句子合併',
            scoreKeys: ['sum_sentence_merge_score', 'sum_SentenceMerge_score']
          },
          {
            key: 'sentence-order',
            groupLabel: '第一部分:單句寫作(50%)',
            groupRowspan: 3,
            showGroup: false,
            typeLabel: 'C.重組',
            scoreKeys: ['sum_sentence_order_score', 'sum_SntenceOrder_score', 'sum_SentenceOrder_score']
          },
          {
            key: 'writing',
            groupLabel: '第二部分:段落寫作(50%)',
            groupRowspan: 1,
            showGroup: true,
            typeLabel: 'D.看圖表寫作',
            scoreKeys: ['sum_writing_score', 'sum_Writing_score']
          }
        ]

        return rows.map(row => ({
          ...row,
          scoreLabel: this.scoreLabelByKeys(row.scoreKeys)
        }))
      },

      speakingSummaryRows () {
        const rows = [
          {
            key: 'repeat',
            groupLabel: '第一、二、三部分',
            groupRowspan: 1,
            showGroup: true,
            typeLabel: '發音、語調、流利度',
            scoreKeys: ['avg_repeat_score', 'Avg_repeat_score', 'Avg_Repeat_score']
          },
          {
            key: 'answer-question',
            groupLabel: '第三部分',
            groupRowspan: 1,
            showGroup: true,
            typeLabel: '文法、字彙之正確性和適切性',
            scoreKeys: ['avg_answerquestion_score', 'Avg_AnswerQuestion_score', 'avg_answer_question_score', 'avgAnswerQuestionScore']
          }
        ]

        return rows.map(row => ({
          ...row,
          scoreLabel: this.scoreLabelByKeys(row.scoreKeys)
        }))
      },

      summaryNote () {
        if (this.isSpeakingReport) {
          const a = this.scoreValueByKeys(['avg_repeat_score', 'Avg_repeat_score', 'Avg_Repeat_score'])
          const b = this.scoreValueByKeys(['avg_answerquestion_score', 'Avg_AnswerQuestion_score', 'avg_answer_question_score', 'avgAnswerQuestionScore'])

          if (a !== null && b !== null) {
            return `GEPT官方網站分數計算公式：百分制總分之計算：（${this.formatScore(a)}+${this.formatScore(b)}）×10分﹦${this.displayTotalScore}分`
          }

          return 'GEPT官方網站分數計算公式：百分制總分之計算：（A+B）×10分'
        }

        if (this.isWritingReport) {
          if (this.isIntermediateWritingReport) {
            return 'GEPT官方網站分數計算公式：【(第一部分之級分)×20分×40%】＋【(第二部分之級分)×20分×60%】'
          }

          return 'GEPT官方網站分數計算公式：{(第一部分得分/30) + (第二部分得分/5)}×50'
        }

        return ''
      },

      displayTotalScore () {
        if (this.result?.total_score) return this.result.total_score
        return null
      },

      reportActionButtons () {        if (!this.isSpeakingReport || !this.isPartReport || !this.typeArr.length) return []

        const currentIndex = this.typeArr.indexOf(this.result?.part)
        const buttons = []
        const previousItem = currentIndex > 0 ? this.typeArr[currentIndex - 1] : null
        const nextItem = currentIndex > -1 && currentIndex < this.typeArr.length - 1
          ? this.typeArr[currentIndex + 1]
          : null

        if (previousItem) {
          buttons.push({ key: 'previous', label: `前往${previousItem}`, target: previousItem })
        }

        buttons.push({ key: 'restart', label: '此題型重新作答', target: this.result?.part })

        if (nextItem) {
          buttons.push({ key: 'next', label: `前往${nextItem}`, target: nextItem })
        }

        return buttons
      },

      typeArr () {
        let self = this
        if (self.tags && self.result) {
          if (this.isSpeakingReport) return this.speakingArr
          if (this.isWritingReport) return this.writingArr
        }
        return []
      },

      speakingArr () {
        let self = this
        if (self.tags) {
          let newArray = self.tags.filter((el) => {
            return el === '複誦' || el === '朗讀句子與短文' || el === '回答問題' || el === '描述圖片' || el === '朗讀短文' || el === '看圖敘述'
          })
          return newArray
        }
        return []
      },

      writingArr () {
        let self = this
        if (self.tags) {
          let newArray = self.tags.filter((el) => {
            return el === '段落寫作' || el === '單句寫作' || el === '中譯英' || el === '英文作文'
          })
          return newArray
        }
        return []
      },

      remainingItems () {
        if (!this.result) return []
        let ar = JSON.parse(JSON.stringify(this.typeArr))

        return ar.filter((item) => item !== this.result.part)
      },

      selectItemData () {
        let self = this
        if (!self.result) return null
        if (self.selectItemIdx > -1 && self.selectChildIdx < 0) {
          return self.result.data[self.selectItemIdx]
        } else if (self.selectItemIdx > -1 && self.selectChildIdx > -1) {
          return self.result.part[self.selectItemIdx].data[self.selectChildIdx]
        }
        return null
      },

      selectItemName () {
        let self = this
        if (self.result && self.selectItemIdx > -1 && self.selectChildIdx > -1) {
          return self.result.part[self.selectItemIdx].name
        }
        return null
      }
    },
    created () {
    },
    mounted () {
      this.startCheckingAuth()
    },
    beforeDestroy () {
      this.sound_effect ? this.audio_stop() : null
      if (this.intervalId) {
        clearInterval(this.intervalId)
      }
    },
    methods: {
      startCheckingAuth() {
        this.intervalId = setInterval(() => {
          this.$store.dispatch('isLoading', true)
          if (this.authorized) {
            clearInterval(this.intervalId)
            this.$route.name === 'gept_report_part' ? this.get_record() : this.get_record_formal_exam()
            this.$route.params.select_id ? this.getContents() : null
          }
        }, 1000)
      },
      async getContents () {
        let path = `/toeic/exams/${this.$route.params.select_id}/contents`
        await api.get(path)
        .then((res) => {
          this.tags = res.data
        })
        .catch((err) => {
        })
      },

      normalizeReportSection (section) {
        const data = Array.isArray(section.data) ? section.data : []
        const group = this.sectionGroup(section.name)
        const questions = data
          .map((question, index) => ({
            ...(question || {}),
            sourceIndex: index,
            key: `${section.sourceIndex}-${index}`
          }))
          .filter(question => !this.isFormalReport || question.user_answer !== null && question.user_answer !== undefined)

        return {
          name: section.name || '',
          sourceIndex: section.sourceIndex,
          key: `${section.sourceIndex}-${section.name || 'section'}`,
          group,
          data: questions
        }
      },

      sectionGroup (name) {
        const singleSentenceNames = ['單句寫作', '句子改寫', '句子合併', '句子重組']
        const paragraphNames = ['段落寫作', '看圖表寫作', '中譯英', '英文作文']

        if (singleSentenceNames.indexOf(name) > -1) return 'single'
        if (paragraphNames.indexOf(name) > -1) return 'paragraph'
        return 'other'
      },

      sectionAnchorId (group) {
        return `gept-report-${group}`
      },

      scrollToReportGroup (group) {
        const target = document.getElementById(this.sectionAnchorId(group))
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      },

      showSectionTitle (section) {
        if (!section) return false
        return true
      },

      sectionDisplayTitle (index, name) {
        return [this.fixedSectionPartLabel(name) || this.partLabel(index), name].filter(Boolean).join('\u3000')
      },

      fixedSectionPartLabel (name) {
        const labelMap = {
          中譯英: '第一部分',
          英文作文: '第二部分'
        }
        return labelMap[name] || ''
      },

      partLabel (index) {
        const labels = ['第一部分', '第二部分', '第三部分', '第四部分', '第五部分', '第六部分']
        return labels[index] || `第${index + 1}部分`
      },

      questionLabel (questionIndex) {
        return `Q${questionIndex + 1}`
      },

      answerLabel (questionIndex) {
        return `A${questionIndex + 1}`
      },

      scoreValueByKeys (keys) {
        const sources = [
          this.result,
          this.result?.scores,
          this.result?.score_detail,
          this.result?.result
        ]

        for (let sourceIndex = 0; sourceIndex < sources.length; sourceIndex++) {
          for (let keyIndex = 0; keyIndex < keys.length; keyIndex++) {
            const value = this.valueByPath(sources[sourceIndex], keys[keyIndex])
            const score = this.toNumber(value)
            if (score !== null) return score
          }
        }

        return null
      },

      scoreLabelByKeys (keys) {
        const score = this.scoreValueByKeys(keys)
        return score === null ? '-' : this.formatScore(score)
      },

      valueByPath (source, path) {
        if (!source || !path) return undefined
        if (Object.prototype.hasOwnProperty.call(source, path)) return source[path]

        return String(path).split('.').reduce((value, key) => {
          if (!value || !Object.prototype.hasOwnProperty.call(value, key)) return undefined
          return value[key]
        }, source)
      },

      summaryGroupLabel (section, groupIndex) {
        if (section.group === 'single') return `${this.partLabel(groupIndex)}-單句寫作(50%)`
        if (section.group === 'paragraph') return `${this.partLabel(groupIndex)}-段落寫作(50%)`
        return `${this.partLabel(groupIndex)}-${section.name}`
      },

      summaryTypeLabel (section, sectionIndex) {
        if (section.group === 'single') {
          return `${String.fromCharCode(65 + sectionIndex)}.${section.name}`
        }
        return section.name || '-'
      },

      sectionScoreLabel (section) {
        const score = this.sectionScoreValue(section)
        return score === null ? '-' : this.formatScore(score)
      },

      sectionScoreValue (section) {
        let hasScore = false
        const total = section.data.reduce((sum, question) => {
          const score = this.questionScoreValue(question)
          if (score === null) return sum
          hasScore = true
          return sum + score
        }, 0)

        return hasScore ? total : null
      },

      sectionScoreValueByName (name) {
        const section = this.visibleReportSections.find(item => item.name === name)
        return section ? this.sectionScoreValue(section) : null
      },

      sectionScoreLabelByName (name) {
        const score = this.sectionScoreValueByName(name)
        return score === null ? '-' : this.formatScore(score)
      },

      questionScoreValue (question) {
        const resultText = this.resultText(question)
        const newScore = resultText ? this.new_result_score(resultText) : null
        if (newScore && this.toNumber(newScore.totalScore) !== null) {
          return this.toNumber(newScore.totalScore)
        }

        const candidates = [
          question?.result?.ers?.Score,
          question?.score,
          question?.user_answer?.score
        ]

        for (let i = 0; i < candidates.length; i++) {
          const score = this.toNumber(candidates[i])
          if (score !== null) return score
        }

        return null
      },

      toNumber (value) {
        if (value === null || value === undefined || value === '') return null
        const number = Number(value)
        return Number.isNaN(number) ? null : number
      },

      formatScore (score) {
        const number = Number(score)
        if (Number.isNaN(number)) return '-'
        return Number.isInteger(number) ? String(number) : String(Number(number.toFixed(1)))
      },

      questionData (question) {
        return question?.result?.question || {}
      },

      questionAudio (question) {
        return this.questionData(question).AudioUrl || ''
      },

      answerAudio (question) {
        return question?.user_answer?.audio || ''
      },

      questionImage (question) {
        return this.questionData(question).ImageUrl || ''
      },

      questionTitle (question) {
        return this.questionData(question).Title || ''
      },

      questionDescription (question) {
        return this.questionData(question).Description || ''
      },

      questionGuide (question) {
        return this.questionData(question).Guide || ''
      },

      questionAnswer (question) {
        return this.questionData(question).Answer || ''
      },

      lineBreak (text) {
        return String(text || '').replace(/(?:\r\n|\r|\n)/g, '<br/>')
      },

      answerPlainText (question) {
        if (this.isSpeakingReport) {
          return question?.result?.ers?.Text || ''
        }

        return question?.result?.ers?.Text ||
          question?.result?.result?.text ||
          question?.result?.result?.Text ||
          question?.result?.result?.stt ||
          question?.result?.result?.sText ||
          question?.result?.text ||
          question?.result?.user_text ||
          question?.result?.Text ||
          question?.user_answer?.text ||
          ''
      },

      resultText (question) {
        const result = question?.result?.result
        if (typeof result === 'string') return result
        if (result && typeof result.result === 'string') return result.result
        return ''
      },

      answerHtml (question) {
        const text = this.answerPlainText(question)
        return text ? this.lineBreak(text) : ''
      },

      hasScoredText (question) {
        return Boolean(question?.result?.ers?.WordScores && question?.result?.ers?.Text)
      },

      scoredWords (question) {
        const text = question?.result?.ers?.Text || ''
        const scores = question?.result?.ers?.WordScores || []
        let wordIndex = 0

        return this.spiltWords(text).map((word) => {
          const isWord = !word.match(/(\s+|[,.!?])/)
          const score = isWord ? Number(scores[wordIndex++]) : null
          return {
            text: word,
            score,
            scoreClass: this.scoreClass(score)
          }
        })
      },

      scoreClass (score) {
        if (score === null || Number.isNaN(score)) return ''
        if (score > 80) return 'good-text'
        if (score > 60) return 'acceptable-text'
        return 'strengthened-text'
      },

      showWordCount (section, question) {
        if (this.isSpeakingReport) return false
        return (section.name === '看圖敘述' || section.name === '段落寫作') && Boolean(this.answerPlainText(question))
      },

      shouldShowReviewButton (section) {
        if (this.isSpeakingReport) {
          const hiddenSpeakingReviewNames = ['複誦', '朗讀句子與短文', '朗讀短文']
          return hiddenSpeakingReviewNames.indexOf(section.name) === -1
        }
        const reviewableNames = ['句子改寫', '句子合併', '句子重組', '回答問題', '看圖敘述', '段落寫作', '中譯英', '英文作文']
        return reviewableNames.indexOf(section.name) > -1
      },

      toggleReportByQuestion (section, question) {
        if (!question.result) return
        if (this.isFormalReport) {
          this.toggleReport_formal(section.sourceIndex, question.sourceIndex)
        } else {
          this.toggleReport(question.sourceIndex)
        }
      },

      showCollapsibleReference (section, question) {
        return !this.isFormalReport && section.name === '看圖敘述' && Boolean(this.questionDescription(question))
      },

      showReferenceAnswerCard (section, question) {
        if (['中譯英', '英文作文'].indexOf(section.name) > -1) return false

        return section.group === 'paragraph' &&
          Boolean(this.questionAnswer(question) || this.questionDescription(question)) &&
          this.isWritingReport &&
          !this.$route.path.includes('quiz')
      },

      countEnglishWords (text) {
        let words = String(text || '').match(/\b[a-zA-Z]+\b/g)
        return words ? words.length : 0
      },

      async toggleMenu (index) {
        let self = this
        if (self.selectedReferenceAnswer === index) {
          self.selectedReferenceAnswer = -1
          self.translate = null
        } else {
          self.selectedReferenceAnswer = index
          self.translate = null
        }
      },

      toggleMenu2 (index, idx) {
        let self = this
        if ((self.selectedReferenceAnswer === index) && (self.selectedReferenceAnswer2 === idx)) {
          self.selectedReferenceAnswer = -1
          self.selectedReferenceAnswer2 = -1
        } else {
          self.selectedReferenceAnswer = index
          self.selectedReferenceAnswer2 = idx
        }

      },

      get_record () {
        let self = this

        let path = `/toeic/exams/${self.$route.params.select_id}/records/${self.$route.params.record_id}`
        self.$store.dispatch('isLoading', true)
        api.get(path)
        .then((res) => {
          self.result = res.data
          self.$store.dispatch('isLoading', false)
        })
        .catch((err) => {
          self.$store.dispatch('isLoading', false)
          console.log(err.response.data.message)
          self.$swal.fire({
            focusConfirm: false,
            confirmButtonText: `確認`,
            title: 'Notice',
            text: err.response.data.message,
            customClass: {
              title: 'text-white',
              container: '',
              popup: 'p-0',
              header: 'swal-header-notice justify-content-center align-items-center',
              closeButton: '',
              htmlContainer: 'font-semibold my-4 text-2xl',
              actions: 'mb-4 mt-2',
              confirmButton: 'btn-submit',
              cancelButton: 'bg-red-main-ai',
            }
          })
        })
      },

      get_record_formal_exam () {
        let self = this

        let path = `/gept/records/${self.$route.params.record_id}`
        self.$store.dispatch('isLoading', true)
        api.get(path)
        .then((res) => {
          self.result = res.data
          self.$store.dispatch('isLoading', false)
        })
        .catch(() => {
          self.$store.dispatch('isLoading', false)
          self.$swal.fire({
            focusConfirm: false,
            confirmButtonText: `確認`,
            title: 'Notice',
            text: '發生錯誤',
            customClass: {
              title: 'text-white',
              container: '',
              popup: 'p-0',
              header: 'swal-header-notice justify-content-center align-items-center',
              closeButton: '',
              htmlContainer: 'font-semibold my-4 text-2xl',
              actions: 'mb-4 mt-2',
              confirmButton: 'btn-submit',
              cancelButton: 'bg-red-main-ai',
            }
          })
        })
      },

      spiltWords (text) {
        let words = String(text || '').split(/(\s+|[,.!?])/).filter(Boolean)
        return words
      },

      checkScore (WordScores, text, child) {
        let parts = String(text || '').split(/(\s+|[,.!?])/).filter(Boolean)

        let wordsOnly = parts.filter(part => !part.match(/(\s+|[,.!?])/))

        let index = wordsOnly.indexOf(child)
        return Number(WordScores?.[index])
      },

      setAudio (url) {
        let self = this
        if (url) {
          if (self.sound_effect) {
            const myName1 = () => {
              return new Promise(async(resolve) => {
                self.audio_stop()
                resolve('ok')
              })
            }

            Promise.race([myName1()])
            .then(() => {
              self.sound_effect = new Audio(url)

              self.audio_play()
            })
          } else {
            self.sound_effect = new Audio(url)

            self.audio_play()
          }
        }
      },

      audio_play () {
        let self = this

        if (!self.sound_effect) return

        self.sound_effect.play()

        self.sound_effect.addEventListener('ended', () => {
          self.audio_stop()
        })
      },

      audio_stop () {
        let self = this
        if (!self.sound_effect) return

        self.sound_effect.pause()
        self.sound_effect.currentTime = 0
      },

      toggleReport (index) {
        let self = this
        if (self.result?.data?.[index]?.result) {
          self.showfirstModal = true

          self.selectItemIdx = index
          self.selectChildIdx = -1
        }
      },

      toggleReport_formal (index, idx) {
        let self = this
        if (self.result?.part?.[index]?.data?.[idx]?.result) {
          self.showfirstModal = true

          self.selectItemIdx = index
          self.selectChildIdx = idx
        }
      },

      handleReportAction (button) {
        if (!button?.target) return

        const query = { q: button.target }
        if (this.$route.query.sid) query.sid = this.$route.query.sid

        this.$router.push({
          name: 'practice_speaking',
          params: {
            select_id: this.$route.params.select_id,
            practice_type: this.result?.type || 'speaking'
          },
          query
        })
      },

      async reStart () {
        window.close()
        // let self = this
        // if (self.$route.name === 'gept_report_part') {
        //   self.$router.push({ name: 'practice_speaking', params: { select_id: self.$route.params.select_id, practice_type: 'speaking'}})
        // } else {
        //   let result = await self.post_records_purchase(self.result.exam_id, self.result.type)
        //   if (result && result.record_id) {
        //     self.$router.push({ name: 'formal_exam', params: { select_id: self.result.exam_id, practice_type: self.result.type }, query: { record_id: self.$route.params.record_id } })
        //   } else {
        //     self.$router.push({ name: 'GEPT_Overview' })
        //   }
        // }
      },

      closeModal () {
        let self = this
        self.showfirstModal = false

        self.selectItemIdx = -1
        self.selectChildIdx = -1
      },

      return_user_text (text) {
        return text.replace(/(?:\r\n|\r|\n)/g, '<br/>')
      },

      chack_user_answer (item) {
        return item?.data?.some(q => q.user_answer !== null) ?? false
      },

      new_result_score (result) {
        let score = 0
        let max_score = 0
        try {
          let parse_result = JSON.parse(result)
          let hasScoreDetail = ['grammar', 'spelling', 'content', 'organization'].some((key) => {
            const viewscore = parse_result?.[key]?.viewscore
            return viewscore !== undefined && viewscore !== null && viewscore !== ''
          })
          if (!hasScoreDetail) return null

          let grammar = parse_result?.grammar?.viewscore ? Number(parse_result?.grammar.viewscore) : 0
          let spelling = parse_result?.spelling?.viewscore ? Number(parse_result?.spelling.viewscore) : 0
          let content = parse_result?.content?.viewscore ? Number(parse_result?.content.viewscore) : 0
          let organization = parse_result?.organization?.viewscore ? Number(parse_result.organization.viewscore) : 0
          score = grammar + spelling + content + organization
          max_score = parse_result.maxScore
          return { totalScore: score, maxScore: max_score }
        } catch (e) {
          return null
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
.gept-report-page {
  color: #1e1e1e;
}

.gept-report-capture {
  max-width: 1180px;
  margin: 0 auto;
}

.report-tabs {
  display: flex;
  justify-content: center;
  gap: 64px;
  margin: 12px 0 32px;
}

.report-tab {
  min-width: 180px;
  border: 0;
  border-radius: 999px;
  background: #0d4f68;
  color: #fff;
  padding: 10px 32px;
  font-weight: 700;
  line-height: 1.2;
  box-shadow: none;

  &:hover {
    background: #0f6584;
  }
}

.report-title-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 78px;
  margin-bottom: 16px;
  background: #fff;

  h1,
  h2 {
    margin: 0;
    color: #111;
    font-size: 24px;
    font-weight: 800;
    line-height: 1.4;
    text-align: center;
  }
}

.report-title-panel--sub {
  margin: 34px 0 16px;

  h2 {
    font-size: 22px;
  }
}

.report-summary {
  display: grid;
  grid-template-columns: minmax(180px, 310px) minmax(0, 1fr);
  gap: 16px;
  margin-bottom: 18px;
}

.report-score-card {
  min-height: 170px;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  span {
    color: #0f6787;
    font-size: 18px;
    font-weight: 800;
    margin-bottom: 12px;
  }

  strong {
    color: #000;
    font-size: 58px;
    font-weight: 800;
    line-height: 1;
  }
}

.report-score-table {
  table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    background: #fff;
    color: #1d1d1d;
    font-weight: 700;
  }

  th {
    background: #126682;
    color: #fff;
    font-size: 18px;
    text-align: center;
    padding: 10px;
    border: 1px solid #75aeca;
  }

  td {
    text-align: center;
    padding: 9px 10px;
    border: 1px solid #75aeca;
    vertical-align: middle;
  }
}

.report-score-note {
  color: #2b7f9e;
  font-size: 13px;
  font-weight: 700;
  margin: 8px 0 0;
}

.report-legend {
  display: flex;
  align-items: center;
  gap: 18px;
  margin: 18px 0;
  font-weight: 700;
}

.legend-item {
  display: flex;
  align-items: center;

  p {
    margin: 0;
  }
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 999px;
  margin-right: 6px;
}

.report-section {
  margin-top: 24px;
}

.report-section__title {
  color: #106a89;
  font-size: 24px;
  font-weight: 800;
  line-height: 1.4;
  margin: 0 0 14px;
}

.qa-card {
  margin-bottom: 14px;
}

.qa-row {
  display: flex;
  width: 100%;
  min-height: 56px;
  background: #fff;
  overflow: hidden;
}

.qa-row--question {
  border-radius: 6px 6px 0 0;
}

.qa-row--answer {
  border-top: 2px solid #dcecf3;
  border-radius: 0 0 6px 6px;
}

.qa-label {
  flex: 0 0 42px;
  width: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 12px;
  font-weight: 800;
}

.qa-label--question {
  background: #6874d6;
}

.qa-label--answer {
  background: #ff563c;
}

.gept-report-page--speaking {
  .qa-label--question {
    background: #2aa1ad;
  }

  .qa-label--answer {
    background: #ff563c;
  }

  .qa-label {
    flex-basis: 48px;
    width: 48px;
  }

  .audio-button {
    width: 60px;
    height: 60px;

    img {
      width: 40px;
    }
  }
}

.qa-body {
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
}

.qa-body--answer {
  justify-content: space-between;
}

.qa-main {
  flex: 1;
  min-width: 0;
  padding: 10px 14px;
}

.qa-text {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.5;
  text-align: left;
  word-break: break-word;
}

.qa-text--answer {
  color: #167394;
}

.qa-text--empty {
  color: #9aa6ad;
}

.audio-button {
  width: 50px;
  height: 50px;
  border: 0;
  background: transparent;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 28px;
    height: auto;
    display: block;
  }
}

.question-image {
  margin-bottom: 10px;
  text-align: center;

  img {
    display: block;
    max-width: 100%;
    max-height: 360px;
    object-fit: contain;
    margin: 0 auto;
  }
}

.answer-count {
  color: #6f7f88;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 4px;
}

.review-button {
  flex: 0 0 auto;
  min-width: 82px;
  margin: 8px 12px 8px 8px;
  border: 1px solid #106a89;
  border-radius: 4px;
  background: #fff;
  color: #106a89;
  font-size: 14px;
  font-weight: 800;
  line-height: 1.2;
  padding: 5px 18px;

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }

  &:not(:disabled):hover {
    background: #106a89;
    color: #fff;
  }
}

.reference-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 4px;
  padding: 8px 12px;
  border-radius: 6px;
  background: #4d9bb6;
  color: #fff;
  font-weight: 800;
  cursor: pointer;

  img {
    width: 14px;
    height: auto;
  }
}

.reference-panel {
  display: none;
  background: #fff;
  border-radius: 6px;
  padding: 18px;
  margin: 4px 0;
  font-weight: 700;

  p {
    margin: 0 0 8px;
    text-align: left;
  }
}

.reference-translation {
  border: 2px dashed #8ebed0;
  background: #eef8fb;
  color: #106a89;
  padding: 14px;
}

.reference-answer-card {
  display: flex;
  overflow: hidden;
  margin-top: 4px;
  border-radius: 6px;
  background: #fff;
}

.reference-answer-card__label {
  flex: 0 0 64px;
  width: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #6c757d;
  color: #fff;
  font-weight: 800;
  text-align: center;
}

.reference-answer-card__body {
  flex: 1;
  padding: 12px 14px;

  p {
    color: #167394;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.5;
    margin: 0 0 8px;
    text-align: left;
  }
}

.report-actions {
  display: flex;
  justify-content: center;
  margin-top: 24px;
}

.report-actions--speaking {
  gap: 64px;
  padding: 26px 0 14px;
}

.report-action-button {
  min-width: 220px;
  border: 0;
  border-radius: 999px;
  background: #0d4f68;
  color: #fff;
  font-size: 16px;
  font-weight: 800;
  line-height: 1.2;
  padding: 14px 28px;

  &:hover {
    background: #0f6584;
  }
}

.download-button {
  border: 1px solid #106a89;
  border-radius: 4px;
  background: #fff;
  color: #106a89;
  font-size: 14px;
  font-weight: 800;
  padding: 10px 48px;

  &:hover {
    background: #106a89;
    color: #fff;
  }
}

.good-bg {
  background-color: rgba(0, 149, 86, 1);
}

.acceptable-bg {
  background-color: rgba(255, 193, 7, 1);
}

.strengthened-bg {
  background-color: rgba(243, 0, 0, 1);
}

.good-text {
  color: rgba(0, 149, 86, 1);
}

.acceptable-text {
  color: rgba(255, 193, 7, 1);
}

.strengthened-text {
  color: rgba(243, 0, 0, 1);
}

@media (max-width: 767px) {
  .report-tabs {
    gap: 12px;
    margin-bottom: 18px;
  }

  .report-tab {
    min-width: 0;
    flex: 1;
    padding: 10px 14px;
  }

  .report-summary {
    grid-template-columns: 1fr;
  }

  .report-score-card {
    min-height: 130px;

    strong {
      font-size: 46px;
    }
  }

  .report-score-table {
    overflow-x: visible;

    table {
      min-width: 0;
    }
  }

  .report-score-note {
    line-height: 1.4;
    word-break: break-word;
  }
.report-title-panel {
    min-height: 64px;

    h1,
    h2 {
      font-size: 20px;
    }
  }

  .report-section__title {
    font-size: 21px;
  }

  .qa-body--answer {
    align-items: stretch;
  }

  .qa-text {
    font-size: 16px;
  }

  .review-button {
    align-self: center;
    min-width: 64px;
    padding: 5px 12px;
  }

  .report-actions--speaking {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .report-action-button {
    min-width: 0;
    width: 100%;
  }
}
</style>
